import { lazy, Suspense } from "react";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { LoadingScreen } from "@/components/ui/LoadingScreen";

// Eagerly load only the Index page (landing page / LCP critical)
import Index from "./pages/Index";

// Lazy load all other pages
const Auth = lazy(() => import("./pages/Auth"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const Contrato = lazy(() => import("./pages/Contrato"));
const CriacaoContas = lazy(() => import("./pages/CriacaoContas"));
const Verificacao = lazy(() => import("./pages/Verificacao"));
const Profile = lazy(() => import("./pages/Profile"));
const Modelo = lazy(() => import("./pages/Modelo"));
const Calendario = lazy(() => import("./pages/Calendario"));
const InicioPostagens = lazy(() => import("./pages/InicioPostagens"));
const EnvioConteudo = lazy(() => import("./pages/EnvioConteudo"));
const ConteudoExclusivo = lazy(() => import("./pages/ConteudoExclusivo"));
const PrimeiraVenda = lazy(() => import("./pages/PrimeiraVenda"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const Afiliado = lazy(() => import("./pages/Afiliado"));
const Banco = lazy(() => import("./pages/Banco"));
const Contato = lazy(() => import("./pages/Contato"));
const Sobre = lazy(() => import("./pages/Sobre"));
const NotFound = lazy(() => import("./pages/NotFound"));
const UniversalChat = lazy(() => import("./pages/UniversalChat"));
const ModelChat = lazy(() => import("./pages/ModelChat"));
const SolicitacaoCadastro = lazy(() => import("./pages/SolicitacaoCadastro"));
const DownloadBlankContract = lazy(() => import("./pages/DownloadBlankContract"));

// Admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminModelos = lazy(() => import("./pages/admin/AdminModelos"));
const AdminModeloDetalhe = lazy(() => import("./pages/admin/AdminModeloDetalhe"));
const AdminPipelines = lazy(() => import("./pages/admin/AdminPipelines"));
const AdminFinanceiro = lazy(() => import("./pages/admin/AdminFinanceiro"));
const AdminUsuarios = lazy(() => import("./pages/admin/AdminUsuarios"));
const AdminFuncionarios = lazy(() => import("./pages/admin/AdminFuncionarios"));
const AdminFuncionarioDetalhe = lazy(() => import("./pages/admin/AdminFuncionarioDetalhe"));
const AdminConfiguracoes = lazy(() => import("./pages/admin/AdminConfiguracoes"));
const AdminSolicitacoes = lazy(() => import("./pages/admin/AdminSolicitacoes"));
const AdminPagamentos = lazy(() => import("./pages/admin/AdminPagamentos"));
const AdminConteudoOculto = lazy(() => import("./pages/admin/AdminConteudoOculto"));
const AdminConteudoExclusivo = lazy(() => import("./pages/admin/AdminConteudoExclusivo"));
const AdminChatterBilling = lazy(() => import("./pages/admin/AdminChatterBilling"));
const AdminSnapsell = lazy(() => import("./pages/admin/AdminSnapsell"));
const AdminAtividadeRecente = lazy(() => import("./pages/admin/AdminAtividadeRecente"));
const AdminTodos = lazy(() => import("./pages/admin/AdminTodos"));

import { AdminProtectedRoute } from "./components/admin/AdminProtectedRoute";

// Employee pages
const EmployeeDashboard = lazy(() => import("./pages/employee/EmployeeDashboard"));
const EmployeeModelos = lazy(() => import("./pages/employee/EmployeeModelos"));
const EmployeeModeloDetalhe = lazy(() => import("./pages/employee/EmployeeModeloDetalhe"));
const EmployeeCalendario = lazy(() => import("./pages/employee/EmployeeCalendario"));
const EmployeeConteudoExclusivo = lazy(() => import("./pages/employee/EmployeeConteudoExclusivo"));
const EmployeeChat = lazy(() => import("./pages/employee/EmployeeChat"));
const EmployeeFinanceiro = lazy(() => import("./pages/employee/EmployeeFinanceiro"));
const EmployeeFinanceiroChat = lazy(() => import("./pages/employee/EmployeeFinanceiroChat"));
const EmployeeConfiguracoes = lazy(() => import("./pages/employee/EmployeeConfiguracoes"));
const EmployeePostManager = lazy(() => import("./pages/employee/EmployeePostManager"));
const EmployeeFuncionarios = lazy(() => import("./pages/employee/EmployeeFuncionarios"));
const EmployeeChatters = lazy(() => import("./pages/employee/EmployeeChatters"));
const EmployeeChatterFines = lazy(() => import("./pages/employee/EmployeeChatterFines"));
const EmployeePagamentos = lazy(() => import("./pages/employee/EmployeePagamentos"));
const EmployeeUsuarios = lazy(() => import("./pages/employee/EmployeeUsuarios"));
const ChatterDailyEntries = lazy(() => import("./pages/employee/ChatterDailyEntries"));
const EmployeeConteudoOculto = lazy(() => import("./pages/employee/EmployeeConteudoOculto"));
const EmployeeConteudoExclusivoAprovacao = lazy(() => import("./pages/employee/EmployeeConteudoExclusivoAprovacao"));
const SupervisorWeeklyEntries = lazy(() => import("./pages/employee/SupervisorWeeklyEntries"));
const ChatterMeeting = lazy(() => import("./pages/employee/ChatterMeeting"));
const SupervisorMeeting = lazy(() => import("./pages/employee/SupervisorMeeting"));
const ChatterWeeklySchedule = lazy(() => import("./pages/employee/ChatterWeeklySchedule"));
const SupervisorScheduleView = lazy(() => import("./pages/employee/SupervisorScheduleView"));
const ChatterBillingHistory = lazy(() => import("./pages/employee/ChatterBillingHistory"));
const EmployeeChatterBilling = lazy(() => import("./pages/employee/EmployeeChatterBilling"));
const EmployeePagamentosSocios = lazy(() => import("./pages/employee/EmployeePagamentosSocios"));
const EmployeeSnapsell = lazy(() => import("./pages/employee/EmployeeSnapsell"));
const EmployeeAtividadeRecente = lazy(() => import("./pages/employee/EmployeeAtividadeRecente"));

import { EmployeeProtectedRoute } from "./components/employee/EmployeeProtectedRoute";

// Recruiter pages
const RecruiterDashboard = lazy(() => import("./pages/recruiter/RecruiterDashboard"));
const RecruiterModelos = lazy(() => import("./pages/recruiter/RecruiterModelos"));
const RecruiterLoja = lazy(() => import("./pages/recruiter/RecruiterLoja"));
const RecruiterProfile = lazy(() => import("./pages/recruiter/RecruiterProfile"));
import { RecruiterProtectedRoute } from "./components/recruiter/RecruiterProtectedRoute";

// Recruiter Admin pages
const RecruiterAdminCalendar = lazy(() => import("./pages/recruiter-admin/RecruiterAdminCalendar"));
const RecruiterAdminAffiliateModels = lazy(() => import("./pages/recruiter-admin/RecruiterAdminAffiliateModels"));
const RecruiterAdminRecruiters = lazy(() => import("./pages/recruiter-admin/RecruiterAdminRecruiters"));
const RecruiterAdminCommissions = lazy(() => import("./pages/recruiter-admin/RecruiterAdminCommissions"));
const RecruiterAdminLoja = lazy(() => import("./pages/recruiter-admin/RecruiterAdminLoja"));
const RecruiterAdminProfile = lazy(() => import("./pages/recruiter-admin/RecruiterAdminProfile"));
const RecruiterAdminMetas = lazy(() => import("./pages/recruiter-admin/RecruiterAdminMetas"));
import { RecruiterAdminProtectedRoute } from "./components/recruiter-admin/RecruiterAdminProtectedRoute";

const queryClient = new QueryClient();

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <PWAUpdatePrompt />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <LanguageProvider>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>
              {/* Public routes */}
              <Route path="/solicitacao" element={<SolicitacaoCadastro />} />
              <Route path="/download-contrato" element={<DownloadBlankContract />} />

              {/* Model/User routes */}
              <Route path="/" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/contrato" element={<Contrato />} />
              <Route path="/criacao-contas" element={<CriacaoContas />} />
              <Route path="/verificacao" element={<Verificacao />} />
              <Route path="/envio-conteudo" element={<EnvioConteudo />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/modelo" element={<Modelo />} />
              <Route path="/calendario" element={<Calendario />} />
              <Route path="/inicio-postagens" element={<InicioPostagens />} />
              <Route path="/conteudo-exclusivo" element={<ConteudoExclusivo />} />
              <Route path="/primeira-venda" element={<PrimeiraVenda />} />
              <Route path="/financeiro" element={<Financeiro />} />
              <Route path="/afiliado" element={<Afiliado />} />
              <Route path="/banco" element={<Banco />} />
              <Route path="/mensagens" element={<ModelChat />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/sobre" element={<Sobre />} />

              {/* Admin routes */}
              <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/modelos" element={<AdminProtectedRoute><AdminModelos /></AdminProtectedRoute>} />
              <Route path="/admin/modelos/:id" element={<AdminProtectedRoute><AdminModeloDetalhe /></AdminProtectedRoute>} />
              <Route path="/admin/funcionarios" element={<AdminProtectedRoute><AdminFuncionarios /></AdminProtectedRoute>} />
              <Route path="/admin/funcionarios/:id" element={<AdminProtectedRoute><AdminFuncionarioDetalhe /></AdminProtectedRoute>} />
              <Route path="/admin/pipelines" element={<AdminProtectedRoute><AdminPipelines /></AdminProtectedRoute>} />
              <Route path="/admin/solicitacoes" element={<AdminProtectedRoute><AdminSolicitacoes /></AdminProtectedRoute>} />
              <Route path="/admin/financeiro" element={<AdminProtectedRoute><AdminFinanceiro /></AdminProtectedRoute>} />
              <Route path="/admin/pagamentos" element={<AdminProtectedRoute><AdminPagamentos /></AdminProtectedRoute>} />
              <Route path="/admin/usuarios" element={<AdminProtectedRoute><AdminUsuarios /></AdminProtectedRoute>} />
              <Route path="/admin/configuracoes" element={<AdminProtectedRoute><AdminConfiguracoes /></AdminProtectedRoute>} />
              <Route path="/admin/conteudo-oculto" element={<AdminProtectedRoute><AdminConteudoOculto /></AdminProtectedRoute>} />
              <Route path="/admin/conteudo-exclusivo-aprovacao" element={<AdminProtectedRoute><AdminConteudoExclusivo /></AdminProtectedRoute>} />
              <Route path="/admin/chat" element={<AdminProtectedRoute><UniversalChat /></AdminProtectedRoute>} />
              <Route path="/admin/chatter-billing" element={<AdminProtectedRoute><AdminChatterBilling /></AdminProtectedRoute>} />
              <Route path="/admin/snapsell" element={<AdminProtectedRoute><AdminSnapsell /></AdminProtectedRoute>} />
              <Route path="/admin/atividade-recente" element={<AdminProtectedRoute><AdminAtividadeRecente /></AdminProtectedRoute>} />
              <Route path="/admin/todos" element={<AdminProtectedRoute><AdminTodos /></AdminProtectedRoute>} />

              {/* Employee routes */}
              <Route path="/employee" element={<EmployeeProtectedRoute><EmployeeDashboard /></EmployeeProtectedRoute>} />
              <Route path="/employee/modelos" element={<EmployeeProtectedRoute><EmployeeModelos /></EmployeeProtectedRoute>} />
              <Route path="/employee/modelos/:id" element={<EmployeeProtectedRoute><EmployeeModeloDetalhe /></EmployeeProtectedRoute>} />
              <Route path="/employee/calendario" element={<EmployeeProtectedRoute><EmployeeCalendario /></EmployeeProtectedRoute>} />
              <Route path="/employee/post-manager" element={<EmployeeProtectedRoute><EmployeePostManager /></EmployeeProtectedRoute>} />
              <Route path="/employee/conteudo-exclusivo" element={<EmployeeProtectedRoute><EmployeeConteudoExclusivo /></EmployeeProtectedRoute>} />
              <Route path="/employee/chat" element={<EmployeeProtectedRoute><EmployeeChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/financeiro" element={<EmployeeProtectedRoute><EmployeeFinanceiro /></EmployeeProtectedRoute>} />
              <Route path="/employee/financeiro-chat" element={<EmployeeProtectedRoute><EmployeeFinanceiroChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/configuracoes" element={<EmployeeProtectedRoute><EmployeeConfiguracoes /></EmployeeProtectedRoute>} />
              <Route path="/employee/mensagens" element={<EmployeeProtectedRoute><UniversalChat /></EmployeeProtectedRoute>} />
              <Route path="/employee/funcionarios" element={<EmployeeProtectedRoute><EmployeeFuncionarios /></EmployeeProtectedRoute>} />
              <Route path="/employee/usuarios" element={<EmployeeProtectedRoute><EmployeeUsuarios /></EmployeeProtectedRoute>} />
              <Route path="/employee/chatters" element={<EmployeeProtectedRoute><EmployeeChatters /></EmployeeProtectedRoute>} />
              <Route path="/employee/chatters/multas" element={<EmployeeProtectedRoute><EmployeeChatterFines /></EmployeeProtectedRoute>} />
              <Route path="/employee/chatters/registros" element={<EmployeeProtectedRoute><ChatterDailyEntries /></EmployeeProtectedRoute>} />
              <Route path="/employee/supervisor/semanal" element={<EmployeeProtectedRoute><SupervisorWeeklyEntries /></EmployeeProtectedRoute>} />
              <Route path="/employee/meeting" element={<EmployeeProtectedRoute><ChatterMeeting /></EmployeeProtectedRoute>} />
              <Route path="/employee/supervisor/meeting" element={<EmployeeProtectedRoute><SupervisorMeeting /></EmployeeProtectedRoute>} />
              <Route path="/employee/horarios" element={<EmployeeProtectedRoute><ChatterWeeklySchedule /></EmployeeProtectedRoute>} />
              <Route path="/employee/supervisor/horarios" element={<EmployeeProtectedRoute><SupervisorScheduleView /></EmployeeProtectedRoute>} />
              <Route path="/employee/pagamentos" element={<EmployeeProtectedRoute><EmployeePagamentos /></EmployeeProtectedRoute>} />
              <Route path="/employee/conteudo-oculto" element={<EmployeeProtectedRoute><EmployeeConteudoOculto /></EmployeeProtectedRoute>} />
              <Route path="/employee/conteudo-exclusivo-aprovacao" element={<EmployeeProtectedRoute><EmployeeConteudoExclusivoAprovacao /></EmployeeProtectedRoute>} />
              <Route path="/employee/meu-faturamento" element={<EmployeeProtectedRoute><ChatterBillingHistory /></EmployeeProtectedRoute>} />
              <Route path="/employee/chatter-billing" element={<EmployeeProtectedRoute><EmployeeChatterBilling /></EmployeeProtectedRoute>} />
              <Route path="/employee/pagamentos-socios" element={<EmployeeProtectedRoute><EmployeePagamentosSocios /></EmployeeProtectedRoute>} />
              <Route path="/employee/snapsell" element={<EmployeeProtectedRoute><EmployeeSnapsell /></EmployeeProtectedRoute>} />
              <Route path="/employee/atividade-recente" element={<EmployeeProtectedRoute><EmployeeAtividadeRecente /></EmployeeProtectedRoute>} />

              {/* Recruiter routes */}
              <Route path="/recruiter" element={<RecruiterProtectedRoute><RecruiterDashboard /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/modelos" element={<RecruiterProtectedRoute><RecruiterModelos /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/loja" element={<RecruiterProtectedRoute><RecruiterLoja /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/perfil" element={<RecruiterProtectedRoute><RecruiterProfile /></RecruiterProtectedRoute>} />
              <Route path="/recruiter/metas" element={<RecruiterProtectedRoute><RecruiterAdminMetas /></RecruiterProtectedRoute>} />

              {/* Recruiter Admin routes */}
              <Route path="/recruiter-admin" element={<RecruiterAdminProtectedRoute><RecruiterAdminCommissions /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/calendario" element={<RecruiterAdminProtectedRoute><RecruiterAdminCalendar /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/modelos-afiliados" element={<RecruiterAdminProtectedRoute><RecruiterAdminAffiliateModels /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/recrutadores" element={<RecruiterAdminProtectedRoute><RecruiterAdminRecruiters /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/loja" element={<RecruiterAdminProtectedRoute><RecruiterAdminLoja /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/perfil" element={<RecruiterAdminProtectedRoute><RecruiterAdminProfile /></RecruiterAdminProtectedRoute>} />
              <Route path="/recruiter-admin/metas" element={<RecruiterAdminProtectedRoute><RecruiterAdminMetas /></RecruiterAdminProtectedRoute>} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ThemeProvider>
);

export default App;